import { GitPullRequest } from 'lucide-react';
import type { ReadonlyURLSearchParams } from 'next/navigation';
import { createElement } from 'react';

import type { Command, CommandGroupName, CommandTag, PaletteMode } from './types';

import { extractTextFromCommandLabel } from './command-label-text';
import { PALETTE_MODE_LIST } from './modes';
import { COMMAND_GROUPS } from './types';

import { isPrV2Route } from '@/lib/pr-v2/route-utils';

export type PaletteSection = {
  id: string;
  heading?: string;
  commands: Command[];
};

export const SEARCH_PULL_REQUESTS_COMMAND_ID = 'command-palette-search-pull-requests';
export const ASK_AI_COMMAND_ID = 'command-palette-ask-ai';

const SECTION_LIMIT = 5;

const GROUP_PRIORITY = new Map<string, number>(
  Object.values(COMMAND_GROUPS).map((group) => [group.name, group.priority]),
);

const ROOT_SEARCH_MODES = new Map<CommandTag, PaletteMode>(
  PALETTE_MODE_LIST.flatMap((mode) => (mode.rootSearch ? [[mode.itemTag, mode] as const] : [])),
);

function matchesRoutePrefix(pathname: string, route: string) {
  if (route === '/') {
    return pathname === '/';
  }

  return pathname === route || pathname.startsWith(`${route}/`);
}

// The group that leads the root list on a page, because its commands act on what the page shows.
const CONTEXT_GROUPS: ReadonlyArray<{
  matches: (pathname: string) => boolean;
  group: CommandGroupName;
  listsSearchOnly: boolean;
}> = [
  { matches: isPrV2Route, group: COMMAND_GROUPS.PR_COMMANDS.name, listsSearchOnly: true },
  {
    matches: (pathname) => matchesRoutePrefix(pathname, '/ai-review'),
    group: COMMAND_GROUPS.AI_REVIEW_SETTINGS.name,
    listsSearchOnly: true,
  },
  // Settings pages lead on /settings, while single settings stay search-only, as there are dozens.
  {
    matches: (pathname) => matchesRoutePrefix(pathname, '/settings'),
    group: COMMAND_GROUPS.SETTINGS.name,
    listsSearchOnly: false,
  },
  // The inbox row selected with the keys, headed by its number.
  {
    matches: (pathname) => matchesRoutePrefix(pathname, '/inbox'),
    group: COMMAND_GROUPS.INBOX_PULL_REQUEST.name,
    listsSearchOnly: false,
  },
];

/** The group whose commands act on what this page shows, if the page has one. */
export function getContextGroup(pathname: string): CommandGroupName | undefined {
  return CONTEXT_GROUPS.find(({ matches }) => matches(pathname))?.group;
}

function isShownOn(
  showOn: Command['showOn'],
  pathname: string,
  searchParams: ReadonlyURLSearchParams | undefined,
) {
  if (typeof showOn === 'function') {
    return showOn(pathname, searchParams);
  }

  if (typeof showOn === 'string') {
    return matchesRoutePrefix(pathname, showOn);
  }

  if (showOn instanceof RegExp) {
    showOn.lastIndex = 0;
    return showOn.test(pathname);
  }

  return true;
}

/** The commands shown on this page. When two registrations share an id, the first one wins. */
export function getVisibleCommands(
  commands: readonly Command[],
  pathname: string,
  searchParams?: ReadonlyURLSearchParams,
): Command[] {
  const seenIds = new Set<string>();

  return commands.filter((command) => {
    if (seenIds.has(command.id) || !isShownOn(command.showOn, pathname, searchParams)) {
      return false;
    }

    seenIds.add(command.id);
    return true;
  });
}

/** What gets ranked: a mode's items inside the mode, or the root's commands and root-searchable items. */
export function getPaletteCandidates(
  commands: readonly Command[],
  mode: PaletteMode | null,
): Command[] {
  if (mode) {
    return commands.filter((command) => command.tag === mode.itemTag);
  }

  return commands.filter((command) => !command.tag || ROOT_SEARCH_MODES.has(command.tag));
}

function getRootSearchKind(command: Command) {
  return command.tag ? ROOT_SEARCH_MODES.get(command.tag)?.rootSearch : undefined;
}

function isPullRequestItem(command: Command) {
  return getRootSearchKind(command) === 'pull-requests';
}

/** "Diff view › Layout" for a mode's option that the root search found. */
export function getRootSearchBreadcrumb(command: Command): string | undefined {
  const mode = command.tag ? ROOT_SEARCH_MODES.get(command.tag) : undefined;
  if (mode?.rootSearch !== 'commands') {
    return undefined;
  }

  // A group named like the mode, as Reviewers is, would otherwise read "Reviewers › Reviewers".
  const group = command.group === mode.badgeLabel ? undefined : command.group;
  return [mode.badgeLabel, group].filter(Boolean).join(' › ');
}

function createSearchPullRequestsCommand(query: string): Command {
  return {
    id: SEARCH_PULL_REQUESTS_COMMAND_ID,
    label: query ? `Search all pull requests for "${query}"` : 'Search pull requests...',
    icon: createElement(GitPullRequest),
    action: () => false,
    enterMode: 'pr-search',
    group: COMMAND_GROUPS.PULL_REQUESTS.name,
  };
}

function groupByPriority(commands: readonly Command[]) {
  const groups = new Map<string, Command[]>();
  for (const command of commands) {
    const group = command.group ?? COMMAND_GROUPS.OTHER.name;
    const members = groups.get(group);
    if (members) {
      members.push(command);
    } else {
      groups.set(group, [command]);
    }
  }

  return [...groups].sort(
    ([groupA], [groupB]) => (GROUP_PRIORITY.get(groupB) ?? 0) - (GROUP_PRIORITY.get(groupA) ?? 0),
  );
}

/** Collects sections in order. A command lands in the first section that takes it, never twice. */
function createSectionList() {
  const sections: PaletteSection[] = [];
  const placedIds = new Set<string>();

  return {
    sections,
    add(
      id: string,
      heading: string | undefined,
      commands: Iterable<Command>,
      limit = Number.POSITIVE_INFINITY,
    ) {
      const taken: Command[] = [];
      for (const command of commands) {
        if (taken.length >= limit) {
          break;
        }
        if (!placedIds.has(command.id)) {
          placedIds.add(command.id);
          taken.push(command);
        }
      }

      if (taken.length > 0) {
        sections.push({ id, heading, commands: taken });
      }
    },
  };
}

export type PaletteSectionsInput = {
  /** `getPaletteCandidates` output, ranked for `getRankingTerm(mode, query)`. */
  ranked: readonly Command[];
  query: string;
  mode: PaletteMode | null;
  pathname: string;
  recentCommandIds: readonly string[];
  recentPullRequestIds: readonly string[];
};

function buildRootBrowseSections({
  ranked,
  pathname,
  recentCommandIds,
  recentPullRequestIds,
}: PaletteSectionsInput): PaletteSection[] {
  const list = createSectionList();
  const commandsById = new Map(ranked.map((command) => [command.id, command]));
  const pick = (ids: readonly string[]) =>
    ids.flatMap((id) => {
      const command = commandsById.get(id);
      return command ? [command] : [];
    });
  const untagged = ranked.filter((command) => !command.tag);
  const context = CONTEXT_GROUPS.find(({ matches }) => matches(pathname));

  // A pull request on the clipboard is why the desktop hotkey opened the palette, so it leads.
  list.add(
    `group:${COMMAND_GROUPS.CLIPBOARD.name}`,
    COMMAND_GROUPS.CLIPBOARD.name,
    untagged.filter((command) => command.group === COMMAND_GROUPS.CLIPBOARD.name),
  );
  if (context) {
    const members = untagged.filter(
      (command) =>
        command.group === context.group && (context.listsSearchOnly || !command.searchOnly),
    );
    list.add(`group:${context.group}`, members[0]?.groupHeading ?? context.group, members);
  }

  if (matchesRoutePrefix(pathname, '/inbox')) {
    const pullRequests = ranked.filter(isPullRequestItem);
    list.add(
      'needs-review',
      'Needs you',
      pullRequests.filter((command) => command.pullRequest?.needsReview),
      SECTION_LIMIT,
    );
    list.add(
      'recent-pull-requests',
      'Recent pull requests',
      pick(recentPullRequestIds).filter(isPullRequestItem),
      SECTION_LIMIT,
    );
  }

  const listed = untagged.filter((command) => !command.searchOnly);
  list.add(
    'recent',
    'Recent',
    pick(recentCommandIds).filter((command) => !command.tag && !command.searchOnly),
  );
  for (const [group, commands] of groupByPriority([
    ...listed,
    createSearchPullRequestsCommand(''),
  ])) {
    list.add(`group:${group}`, group, commands);
  }

  return list.sections;
}

function buildRootSearchSections({ ranked, query }: PaletteSectionsInput) {
  const commands: PaletteSection = {
    id: 'commands',
    heading: 'Commands',
    commands: ranked.filter((command) => !command.tag || getRootSearchKind(command) === 'commands'),
  };
  const pullRequests: PaletteSection = {
    id: 'pull-requests',
    heading: COMMAND_GROUPS.PULL_REQUESTS.name,
    commands: [
      ...ranked.filter(isPullRequestItem).slice(0, SECTION_LIMIT),
      createSearchPullRequestsCommand(query),
    ],
  };
  // The section holding the best match leads. With no matching pull request, that is the commands.
  const bestMatch = ranked[0];
  const sections =
    bestMatch && isPullRequestItem(bestMatch) ? [pullRequests, commands] : [commands, pullRequests];

  return sections.filter((section) => section.commands.length > 0);
}

// "Other" holds a mode's ungrouped rows, like "Reset diff settings to defaults", and names nothing.
function getModeGroupHeading(group: string | undefined) {
  return group === COMMAND_GROUPS.OTHER.name ? undefined : group;
}

/** The palette's list: its sections, and the number each row of a numbered mode shows. */
type PaletteList = {
  sections: PaletteSection[];
  ordinals: ReadonlyMap<string, number>;
};

const NO_ORDINALS: ReadonlyMap<string, number> = new Map();

function buildModeSections({
  ranked,
  query,
  mode,
}: Pick<PaletteSectionsInput, 'ranked' | 'query'> & { mode: PaletteMode }): PaletteList {
  if (!query) {
    const sections = groupByPriority(ranked).map(([group, commands]) => ({
      id: `group:${group}`,
      heading: commands[0]?.groupHeading ?? getModeGroupHeading(group),
      commands,
    }));
    return { sections, ordinals: mode.numbered ? getModeOrdinals(sections) : NO_ORDINALS };
  }

  // A digit keeps only the row it numbers, and that row keeps showing its number.
  const ordinal = getQueryOrdinal(mode, query);
  const ordinals =
    ordinal === null ? NO_ORDINALS : buildModeSections({ ranked, query: '', mode }).ordinals;
  const results =
    ordinal === null ? ranked : ranked.filter((command) => ordinals.get(command.id) === ordinal);
  if (results.length === 0) {
    return { sections: [], ordinals };
  }

  const groups = new Set(results.map((command) => command.group ?? COMMAND_GROUPS.OTHER.name));
  const heading =
    groups.size === 1
      ? (results[0]?.groupHeading ?? getModeGroupHeading([...groups][0]))
      : undefined;
  return { sections: [{ id: 'results', heading, commands: [...results] }], ordinals };
}

/** The row a query picks by number in a numbered mode: a single digit, 1 to 9. */
function getQueryOrdinal(mode: PaletteMode | null, query: string): number | null {
  const term = query.trim();
  return mode?.numbered && /^[1-9]$/.test(term) ? Number(term) : null;
}

/** The numbers a numbered mode's rows show: 1 to 9 down its list for an empty search. */
function getModeOrdinals(browseSections: readonly PaletteSection[]): ReadonlyMap<string, number> {
  const browseOrder = browseSections.flatMap((section) => section.commands);
  return new Map(browseOrder.slice(0, 9).map((command, index) => [command.id, index + 1]));
}

/**
 * What the ranker searches for. A digit that picks a row of a numbered mode ranks like no search,
 * so the rows keep the order their numbers count down.
 */
export function getRankingTerm(mode: PaletteMode | null, query: string): string {
  return getQueryOrdinal(mode, query) === null ? query : '';
}

/** The palette's list, as ordered sections, with the numbers a numbered mode's rows show. */
export function buildPaletteSections(input: PaletteSectionsInput): PaletteList {
  // The ranker ignores a query of spaces, so the list has to treat it as no query too.
  const query = input.query.trim();
  if (input.mode) {
    return buildModeSections({ ranked: input.ranked, query, mode: input.mode });
  }

  return {
    sections: query ? buildRootSearchSections({ ...input, query }) : buildRootBrowseSections(input),
    ordinals: NO_ORDINALS,
  };
}

/**
 * Lists the rows already shown for this query first, in their order, then rows that arrived since
 * (GitHub search results), so a late result never moves the rows under the highlight.
 */
export function keepShownOrder<T extends Command>(
  ranked: readonly T[],
  shownIds: readonly string[],
): T[] {
  const shownPosition = new Map(shownIds.map((id, index) => [id, index]));

  return ranked
    .map((command, rank) => ({
      command,
      position: shownPosition.get(command.id) ?? shownIds.length + rank,
    }))
    .sort((a, b) => a.position - b.position)
    .map(({ command }) => command);
}

/** Where a mode's source stands, read from the sentinel commands its module registers. */
export function getModeSourceState(commands: readonly Command[], mode: PaletteMode) {
  const hasTag = (tag: CommandTag | undefined) =>
    tag !== undefined && commands.some((command) => command.tag === tag);
  const emptySentinel = commands.find(
    (command) => mode.emptyTag !== undefined && command.tag === mode.emptyTag,
  );
  const hasLoaded = hasTag(mode.itemTag) || emptySentinel !== undefined || hasTag(mode.pendingTag);
  const isFetching = hasTag(mode.loadingTag) || hasTag(mode.pendingTag);

  return {
    hasLoaded,
    // Only a mode with a pending tag searches remotely, and it keeps its local rows meanwhile.
    isSearchingRemotely: hasLoaded && isFetching && mode.pendingTag !== undefined,
    emptyMessage:
      (emptySentinel && extractTextFromCommandLabel(emptySentinel.label)) || mode.emptyMessage,
  };
}
